import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Helper de stockage natif mobile basé sur @capacitor/preferences.
 * Maintient un cache mémoire synchronisé avec localStorage pour un accès
 * ultra-rapide dans les intercepteurs et les hooks tout en garantissant la
 * persistance native sur Android (SharedPreferences) même après mise en veille
 * prolongée ou nettoyage du WebView.
 */
const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const PERSISTED_KEYS = [
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  'guest_id',
  'guest_interactions',
  'streak_data',
  'pepite_streak_data',
  'pepite_onboarding_tasks',
  'is_authenticated',
] as const;

const memoryCache: Record<string, string | null> = {};
let isInitialized = false;
let initPromise: Promise<void> | null = null;

export const isNativeMobile = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Hydrate le cache mémoire et localStorage depuis @capacitor/preferences au démarrage sur mobile.
 */
export const initMobileStorage = async (): Promise<void> => {
  if (!isNativeMobile()) {
    isInitialized = true;
    return;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      await Promise.all(
        PERSISTED_KEYS.map(async (key) => {
          const { value } = await Preferences.get({ key });
          if (value !== null && value !== undefined) {
            memoryCache[key] = value;
            try {
              localStorage.setItem(key, value);
            } catch {
              // ignore storage quota errors
            }
          } else {
            const localVal = localStorage.getItem(key);
            if (localVal !== null) {
              memoryCache[key] = localVal;
              await Preferences.set({ key, value: localVal });
            }
          }
        })
      );
      // Synchronise l'alias streak_data <-> pepite_streak_data
      if (memoryCache['pepite_streak_data'] && !memoryCache['streak_data']) {
        memoryCache['streak_data'] = memoryCache['pepite_streak_data'];
        await Preferences.set({ key: 'streak_data', value: memoryCache['pepite_streak_data'] });
      } else if (memoryCache['streak_data'] && !memoryCache['pepite_streak_data']) {
        memoryCache['pepite_streak_data'] = memoryCache['streak_data'];
        localStorage.setItem('pepite_streak_data', memoryCache['streak_data']);
      }
    } catch (error) {
      console.warn('[mobileStorage] Failed to initialize Preferences:', error);
    } finally {
      isInitialized = true;
    }
  })();

  return initPromise;
};

export const mobileStorage = {
  isNative: isNativeMobile,

  async getItem(key: string): Promise<string | null> {
    if (!isNativeMobile()) {
      return localStorage.getItem(key);
    }
    if (key in memoryCache && memoryCache[key] !== undefined) {
      return memoryCache[key];
    }
    try {
      const { value } = await Preferences.get({ key });
      if (value !== null && value !== undefined) {
        memoryCache[key] = value;
        localStorage.setItem(key, value);
        return value;
      }
    } catch (err) {
      console.warn(`[mobileStorage] Error reading key "${key}":`, err);
    }
    return localStorage.getItem(key);
  },

  getItemSync(key: string): string | null {
    if (key in memoryCache && memoryCache[key] !== undefined) {
      return memoryCache[key];
    }
    return localStorage.getItem(key);
  },

  async setItem(key: string, value: string): Promise<void> {
    memoryCache[key] = value;
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
    if (!isNativeMobile()) return;
    try {
      await Preferences.set({ key, value });
      if (key === 'pepite_streak_data') {
        memoryCache['streak_data'] = value;
        await Preferences.set({ key: 'streak_data', value });
      }
    } catch (err) {
      console.warn(`[mobileStorage] Error writing key "${key}":`, err);
    }
  },

  async removeItem(key: string): Promise<void> {
    delete memoryCache[key];
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
    if (!isNativeMobile()) return;
    try {
      await Preferences.remove({ key });
    } catch (err) {
      console.warn(`[mobileStorage] Error removing key "${key}":`, err);
    }
  },

  async getAuthToken(): Promise<string | null> {
    if (!isInitialized && isNativeMobile()) {
      await initMobileStorage();
    }
    return this.getItem(AUTH_TOKEN_KEY);
  },

  getAuthTokenSync(): string | null {
    return this.getItemSync(AUTH_TOKEN_KEY);
  },

  async getRefreshToken(): Promise<string | null> {
    if (!isInitialized && isNativeMobile()) {
      await initMobileStorage();
    }
    return this.getItem(REFRESH_TOKEN_KEY);
  },

  async setTokens(authToken?: string | null, refreshToken?: string | null): Promise<void> {
    if (authToken) {
      await this.setItem(AUTH_TOKEN_KEY, authToken);
    }
    if (refreshToken) {
      await this.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },

  async clearTokens(): Promise<void> {
    await this.removeItem(AUTH_TOKEN_KEY);
    await this.removeItem(REFRESH_TOKEN_KEY);
  },
};

export default mobileStorage;
